import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Calendar, Clock, ArrowLeft, ArrowRight, Newspaper, BookOpen, ChevronRight, Sparkles } from 'lucide-react';
import { SiteHeader } from './ServicesIndex';
import SiteFooter from '../components/SiteFooter';
import useSeo from '../hooks/useSeo';
import { getArticleBySlug, getRelatedArticles } from '../data/blogArticles';

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

// Render one section block (body paragraphs, list, faq or callout)
function Section({ section, idx }) {
  const { heading, body, list, faq, callout } = section;
  return (
    <section className="mb-9" data-testid={`article-section-${idx}`}>
      {heading && (
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 leading-tight mb-3" style={{ fontFamily: '"Playfair Display", serif' }}>
          {heading}
        </h2>
      )}
      {Array.isArray(body) &&
        body.map((p, i) => (
          <p key={i} className="text-[15px] sm:text-base text-slate-700 leading-relaxed mb-3">
            {p}
          </p>
        ))}
      {Array.isArray(list) && (
        <ul className="mt-3 space-y-2.5">
          {list.map((li, i) => (
            <li key={i} className="flex items-start gap-3 text-[15px] text-slate-700 leading-relaxed">
              <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-brand-green shrink-0" />
              <span>{li}</span>
            </li>
          ))}
        </ul>
      )}
      {Array.isArray(faq) && (
        <div className="mt-3 space-y-3">
          {faq.map((qa, i) => (
            <details key={i} className="group rounded-xl border border-slate-200 bg-slate-50 p-4 hover:border-brand-green/40 transition-colors">
              <summary className="cursor-pointer list-none flex items-start gap-3 text-slate-900 font-semibold">
                <ChevronRight size={16} className="mt-1 text-brand-green transition-transform group-open:rotate-90 shrink-0" />
                <span>{qa.q}</span>
              </summary>
              <p className="mt-2 ml-7 text-[14px] sm:text-[15px] text-slate-700 leading-relaxed">{qa.a}</p>
            </details>
          ))}
        </div>
      )}
      {callout && (
        <div className="mt-4 flex items-start gap-3 rounded-xl bg-brand-green/10 border-l-4 border-brand-green px-4 py-3.5">
          <Sparkles size={18} className="mt-0.5 text-brand-green shrink-0" />
          <p className="text-[14px] sm:text-[15px] text-slate-800 leading-relaxed font-medium">{callout}</p>
        </div>
      )}
    </section>
  );
}

export default function BlogArticle({ onOpenCalculator, onOpenAuth }) {
  const { slug } = useParams();
  const [remoteArticle, setRemoteArticle] = useState(undefined);
  const fallbackArticle = getArticleBySlug(slug);
  useEffect(() => { let active = true; axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/blog/posts/${slug}`).then((res) => { if (active) setRemoteArticle({ ...res.data, publishedOn: res.data.published_at, readMinutes: res.data.read_minutes || 1, heroAccent: 'from-brand-blue via-blue-800 to-slate-900', dateline: res.data.category_label, featuredImage: res.data.featured_image_url ? `${process.env.REACT_APP_BACKEND_URL}${res.data.featured_image_url}` : null }); }).catch(() => { if (active) setRemoteArticle(null); }); return () => { active = false; }; }, [slug]);
  const article = remoteArticle || fallbackArticle;

  useSeo(
    article?.title || 'Blog & News',
    article?.excerpt ||
      'Fresh pest control news, expert guides and FAQs from The Indian Pest Control.'
  );

  // Push GTM dataLayer event whenever an article is opened.
  useEffect(() => {
    if (!article) return;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'blog_article_view',
        article_slug: article.slug,
        article_title: article.title,
        article_type: article.type,
        article_pest: article.pest,
        page_location: window.location.href,
        page_path: window.location.pathname,
      });
    } catch (e) { /* noop */ }
  }, [article]);

  if (remoteArticle === undefined && !fallbackArticle) return <div className="min-h-screen bg-white" data-testid="article-loading" />;
  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const isNews = article.type === 'news';
  const related = getRelatedArticles(article, 3);

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: 'Poppins, Inter, system-ui, sans-serif' }}>
      <SiteHeader onOpenCalculator={onOpenCalculator} onOpenAuth={onOpenAuth} />

      {/* ── ARTICLE HERO ──────────────────────────────────────── */}
      <header
        className={`relative overflow-hidden text-white bg-gradient-to-br ${article.heroAccent}`}
        data-testid="article-hero"
        style={article.featuredImage ? { backgroundImage: `linear-gradient(rgba(15,23,42,.48), rgba(15,23,42,.78)), url(${article.featuredImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
      >
        {isNews && (
          <div
            aria-hidden
            className="absolute inset-0 opacity-25"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 6px)',
            }}
          />
        )}
        <div className="relative max-w-4xl mx-auto px-4 py-10 sm:py-14">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-[12px] sm:text-sm text-white/85 hover:text-white font-semibold uppercase tracking-wider"
            data-testid="article-back-to-blog"
          >
            <ArrowLeft size={14} /> Back to Blog &amp; News
          </Link>

          <div className="mt-5 inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.25em] bg-white/15 border border-white/20 backdrop-blur-sm rounded-full px-3 py-1.5">
            {isNews ? (
              <>
                <Newspaper size={12} /> Current News
                {article.dateline && <span className="opacity-80">· {article.dateline}</span>}
              </>
            ) : (
              <>
                <BookOpen size={12} /> Expert Guide
              </>
            )}
          </div>

          <h1
            className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight"
            style={{ fontFamily: '"Playfair Display", "Cormorant Garamond", serif' }}
          >
            {article.title}
          </h1>

          {article.excerpt && (
            <p className="mt-4 text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl">
              {article.excerpt}
            </p>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] sm:text-sm text-white/80 font-medium">
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={13} /> {formatDate(article.publishedOn)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={13} /> {article.readMinutes} min read
            </span>
            {article.author && <span>· By {article.author}</span>}
          </div>
        </div>
      </header>

      {/* ── ARTICLE BODY ──────────────────────────────────────── */}
      <article className="max-w-3xl mx-auto px-4 py-10 sm:py-14" data-testid="article-body">
        {isNews && (
          <div className="mb-8 flex items-center gap-3 pb-4 border-b border-slate-200">
            <div className="text-3xl font-black tracking-tight text-stone-900" style={{ fontFamily: '"Playfair Display", serif' }}>
              The Pest Control Times
            </div>
            <div className="ml-auto text-[10px] uppercase tracking-[0.25em] text-stone-500 font-semibold">
              Edition · {formatDate(article.publishedOn)}
            </div>
          </div>
        )}

        {article.content_html ? <div className="prose prose-slate max-w-none prose-headings:font-bold prose-a:text-brand-green" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content_html) }} data-testid="cms-article-content" /> : Array.isArray(article.sections) ? article.sections.map((s, i) => <Section key={i} section={s} idx={i} />) : <p className="text-base leading-relaxed text-slate-600" data-testid="cms-article-empty">This article is being prepared. Please check back soon.</p>}

        {/* CTA card */}
        <div className="mt-12 rounded-2xl bg-gradient-to-br from-brand-green/10 via-emerald-50 to-white border border-brand-green/20 px-5 sm:px-7 py-6 sm:py-7">
          <h3 className="text-lg sm:text-xl font-bold text-slate-900">
            Need professional help with this pest?
          </h3>
          <p className="text-sm sm:text-[15px] text-slate-700 mt-1.5 leading-relaxed">
            Book a free inspection with The Indian Pest Control&apos;s certified team. Transparent pricing, written warranty, eco-friendly chemicals.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => onOpenCalculator && onOpenCalculator()}
              className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-brand-green hover:bg-brand-green-dark text-white font-semibold shadow-md transition-colors"
              data-testid="article-cta-book"
            >
              Book a free inspection <ArrowRight size={16} />
            </button>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-white border border-slate-200 hover:border-brand-green hover:text-brand-green text-slate-700 font-semibold transition-colors"
              data-testid="article-cta-more"
            >
              More articles
            </Link>
          </div>
        </div>
      </article>

      {/* ── RELATED ARTICLES ───────────────────────────────────── */}
      {related.length > 0 && (
        <section className="bg-slate-50 border-t border-slate-200">
          <div className="max-w-5xl mx-auto px-4 py-10 sm:py-14">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-5" style={{ fontFamily: '"Playfair Display", serif' }}>
              You might also like
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  data-testid={`related-${r.slug}`}
                  className="group block bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all"
                >
                  <div className={`h-20 bg-gradient-to-br ${r.heroAccent}`} />
                  <div className="px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                      {r.type === 'news' ? 'News' : 'Guide'} · {r.readMinutes} min
                    </div>
                    <h3 className="text-[14px] font-semibold text-slate-900 leading-snug line-clamp-2 group-hover:text-brand-green transition-colors">
                      {r.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <SiteFooter />
    </div>
  );
}
